import React, { RefObject } from 'react';

import { margin, useClassnames } from '@lumapps/classnames';
import { isInDesignerMode as isInDesignerModeSelector } from '@lumapps/contents/ducks/selectors';
import { useHeaderFixedHeight } from '@lumapps/lumx-hooks/hooks/useHeaderHeight';
import { Menubar } from '@lumapps/lumx-menu/components/Menubar';
import { Kind, Message, Size, Theme, useTheme } from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { useDimensions } from '@lumapps/responsive';
import { useTranslate } from '@lumapps/translations';
import { ToolbarItem } from '@lumapps/wrex/components/Toolbar/ToolbarItem';
import { WREX_CORE } from '@lumapps/wrex/keys';

import { STRUCTURED_CONTENT_TOOLBAR_BREAKPOINTS, STRUCTURED_CONTENT_TOOLBAR_CLASSNAME } from '../../../constants';
import { STRUCTURED_CONTENT } from '../../../keys';
import { useStructuredContentToolbar } from './useStructuredContentToolbar';

import './index.scss';

interface StructuredContentToolbarProps {
    className?: string;
    parentDialogRef?: RefObject<HTMLDivElement>;
    isReadOnly?: boolean;
}

export const StructuredContentToolbar: React.FC<StructuredContentToolbarProps> = ({
    className,
    parentDialogRef,
    isReadOnly,
}) => {
    const theme = useTheme() || Theme.light;
    const { currentBreakpoint, ref: dimensionRef } = useDimensions({
        breakpoints: STRUCTURED_CONTENT_TOOLBAR_BREAKPOINTS,
    });

    const {
        GoogleUploadDialog,
        toolbarButtons,
        insertFileHiddenInput,
        insertImageGalleryHiddenInput,
        insertImageHiddenInput,
        uploadImageDialog,
        insertEmojiPicker,
        insertLinkDialog,
        insertEmbeddedMapDialog,
        insertImageGalleryDialog,
        insertLinkVideoDialog,
        videoUploadDialog,
        videoUploadInput,
        insertPlayVideoDialog,
        oneDrivePicker,
    } = useStructuredContentToolbar({ parentDialogRef, theme, isReadOnly, currentBreakpoint });

    const { translateKey } = useTranslate();
    const { block, element } = useClassnames(STRUCTURED_CONTENT_TOOLBAR_CLASSNAME);

    const isInDesignerMode = useSelector(isInDesignerModeSelector);
    const headerHeight = useHeaderFixedHeight();

    const toolbarTopPositionOverride = isInDesignerMode ? headerHeight : undefined;

    return (
        <>
            <div
                ref={dimensionRef}
                className={block([className])}
                style={{
                    top: toolbarTopPositionOverride,
                }}
            >
                <Menubar
                    className={element('menubar')}
                    ariaLabel={translateKey(WREX_CORE.EDITOR_TOOLBAR_LABEL)}
                    size={Size.s}
                >
                    {toolbarButtons.map((item: any) => (
                        <ToolbarItem {...item} key={`toolbar-item-${item.itemKey}`} />
                    ))}
                </Menubar>
                {oneDrivePicker}
                {insertFileHiddenInput}
                {insertImageHiddenInput}
                {uploadImageDialog}
                {insertImageGalleryHiddenInput}
                {insertLinkDialog}
                {insertEmbeddedMapDialog}
                {insertLinkVideoDialog}
                {videoUploadDialog}
                {videoUploadInput}
                {insertPlayVideoDialog}
                {insertImageGalleryDialog}
                {insertEmojiPicker}
                {GoogleUploadDialog}
            </div>

            {currentBreakpoint === 'too-small' && (
                <Message kind={Kind.warning} hasBackground className={margin('top', Size.big)}>
                    {translateKey(STRUCTURED_CONTENT.WARNING_MESSAGE_SMALL_DISPLAY)}
                </Message>
            )}
        </>
    );
};

StructuredContentToolbar.displayName = 'StructuredContentToolbar';
